import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDate } from '../../../hooks/useDate';
import { useTranslator } from '../../../hooks/useTranslator';

export class FileWithMetadata extends File {
    duration = 0;
    __is_file_with_metadata__ = true;
    // eslint-disable-next-line
    constructor(fileBits, fileName, options) {
        super(fileBits, fileName, options);
    }

    static fromFile(file) {
        return new FileWithMetadata([file], file.name, { type: file.type });
    }

    static fromAudioFile(file, duration) {
        const fileWithMetadata = new FileWithMetadata([file], file.name, { type: file.type });
        fileWithMetadata.duration = duration;
        return fileWithMetadata;
    }

    static fromBlob(blob, fileName, type) {
        return new FileWithMetadata([blob], fileName, { type });
    }
}

export const uploadfileToAsset = async ({ file, mutation, super_power, class_profile_id }) => {
    const formDataToSend = new FormData();
    formDataToSend.append("asset_file", file);
    formDataToSend.append("class_profile_id", class_profile_id);
    formDataToSend.append(
        "super_power", super_power ?? ""
    );
    return await mutation(formDataToSend);
};

export const createTxT = (contenido) => {
    const blob = new Blob([contenido], { type: 'text/plain' });
    return blob;
}

export const useFileSelector = ({ accept, multiple }) => {
    const fileInputRef = useRef(null);
    const [value, setValue] = useState(null);

    const reset = () => {
        setValue(null);
    }

    const openFileSelector = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        if (multiple) {
            const files = event.target.files;
            if (files.length > 0) {
                setValue(files);
            }
        } else {
            const file = event.target.files[0];
            if (file) {
                setValue(file);
            }
        }
    };

    return {
        fileInputRef, openFileSelector, accept, multiple, handleFileChange, reset, value, bind: {
            fileInputRef, openFileSelector, accept, multiple, handleFileChange, reset, value
        }
    };
}

export const FileSelector = ({ Button, openFileSelector, accept, multiple, fileInputRef, handleFileChange }) => {
    return (
        <>
            <Button onClick={openFileSelector} />
            <input
                multiple={multiple}
                accept={accept}
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
        </>
    );
};

export const AudioSvg1 = () => {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 18H9V6H7V18ZM11 22H13V2H11V22ZM3 14H5V10H3V14ZM15 18H17V6H15V18ZM19 10V14H21V10H19Z" fill="white" />
        </svg>
    )
}

export const RemoveSvg1 = () => {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 9V19H8V9H16ZM14.5 3H9.5L8.5 4H5V6H19V4H15.5L14.5 3ZM18 7H6V19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7Z" fill="white" />
        </svg>

    );
}

export const AudioSvg2 = () => {
    return (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M23.9927 30.126H15.5581C11.8728 30.126 8.88533 27.1385 8.88533 23.4533V8.54674C8.88533 4.86148 11.8728 1.87398 15.5581 1.87398H21.2001L30.6655 11.3393V23.4533C30.6655 27.1385 27.678 30.126 23.9927 30.126Z" fill="#EEF1F7" stroke="#CBD0DC" stroke-width="2.6691" />
            <rect y="14.2031" width="21.5729" height="12.5842" rx="4.67093" fill="#D5B06B" />
        </svg>
    );
}

/**
 * Converts a file size in bytes to megabytes or gigabytes.
 *
 * @param {number} size - The file size in bytes.
 * @returns {Object} An object containing the original size and the converted size in either megabytes or gigabytes.
 * @property {number} size - The original file size in bytes.
 * @property {string} [sizeinmb] - The file size in megabytes, if the size is less than 1e9 bytes.
 * @property {string} [sizeingb] - The file size in gigabytes, if the size is 1e9 bytes or more.
 */
export const sizeTombandgb = (size) => {
    if (size < 1e9) {
        return { size: size, sizeinmb: (size / 1e6).toFixed(2) };
    }
    return { size: size, sizeingb: (size / 1e9).toFixed(2) };
}

export const getFileSize = (file) => {
    if (!file) return { size: 0, sizeinmb: 0 };
    const size = file.size;
    return sizeTombandgb(size);
}

export const getMetaDataFromVideo = (videoPath, type) => new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.style.display = 'none';
    const source = document.createElement('source');
    source.src = videoPath;
    source.type = type;
    video.appendChild(source);
    document.body.appendChild(video);
    video.addEventListener('loadeddata', () => {
        video.play();
    });
    video.addEventListener('error', (event) => {
        reject(event);
    });
    video.addEventListener('timeupdate', () => {
        if (video.currentTime > 10) {
            reject("Video is too long");
            video.remove();
        }
        if (video.duration && video.duration !== Infinity) {
            resolve({ duration: video.duration });
            video.pause();
            video.remove();
        }
    });
    video.volume = 0;
    video.load();
});


export const getDuration = async (file) => {
    if (!file) {
        return 0;
    }
    if (file.__is_file_with_metadata__) {
        return file.duration;
    }
    const url = URL.createObjectURL(file);
    const metadata = await getMetaDataFromVideo(url, file.type).catch(() => null);
    return metadata?.duration ?? 0

};

export function formatTime(seconds) {
    if (!seconds) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(remainingSeconds).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
}

export const DurationComponent1 = ({ file }) => {
    const [duration, setDuration] = useState();
    useEffect(() => {
        getDuration(file).then((duration) => setDuration(formatTime(duration)))
    }, [file]);
    if (!duration) return null;
    return <label className='text-3'>{duration}</label>;
}

export const FileCard = ({ file, onRemove, className, Message, classNameIcon }) => {
    const { sizeinmb, sizeingb } = getFileSize(file);
    return (
        <div className={classNames("flex w-full items-center gap-4 bg-DarkBlue-700 text-white p-4 rounded-4", className)}>
            <div className={classNames("bg-LightBlue-500 w-12 h-12 rounded-4 flex justify-center items-center", classNameIcon)}>
                <AudioSvg1 />
            </div>
            <div className='flex flex-col flex-1'>
                <label className='text-4'>{file.name}</label>
                {Message ? <Message /> :
                    <label className='text-3 gap-4'><DurationComponent1 file={file} />&nbsp;|&nbsp;{sizeinmb ?? sizeingb} {sizeingb ? "GB" : "MB"}</label>}
            </div>
            {Message ? null : <div onClick={onRemove} className='bg-Red-450/50 flex justify-center items-center w-8 h-8 rounded-2'>
                <RemoveSvg1 />
            </div>}
        </div>
    );
}

export const FileCardAws = ({ file }) => {
    const { dateToString } = useDate();
    const date = file?.params?.LastModified ? dateToString(new Date(file?.params?.LastModified)) : null;
    const name = file?.params?.Key?.split('.')?.[0]?.split('_')?.reverse?.()?.[0];
    return (
        <div className={"flex w-full items-center gap-4 bg-LightBlue-175 text-white p-4 rounded-4"}>
            <div className={"bg-LightBlue-500 w-12 h-12 rounded-4 flex justify-center items-center"}>
                <AudioSvg2 />
            </div>
            <div className='flex flex-col flex-1'>
                <label className='text-4 text-bold'>{name}</label>
                <label className='text-3'>{date}</label>
            </div>
        </div>
    );
}

export const AudioIconSvg = () => {
    return (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M23.9927 30.1261H15.5581C11.8728 30.1261 8.88533 27.1386 8.88533 23.4534V8.54686C8.88533 4.8616 11.8728 1.8741 15.5581 1.8741H21.2001L30.6655 11.3395V23.4534C30.6655 27.1386 27.678 30.1261 23.9927 30.1261Z" fill="#EEF1F7" stroke="#CBD0DC" stroke-width="2.6691" />
            <rect y="14.2031" width="21.5729" height="12.5842" rx="4.67093" fill="#D5B06B" />
        </svg>
    );
}

export const AudioFileState = ({ data,MenuAudioFileState }) => {
    const { t } = useTranslator();
    const { dateToString } = useDate();
    const date = useMemo(() => {
        return data?.LastModified ? dateToString(new Date(data?.LastModified)) : null;
    }, [data?.LastModified, dateToString])
    const name = useMemo(() => {
        return data?.Key?.split('.')?.[0]?.split('_')?.reverse?.()?.[0];
    }, [data?.Key])
    const state = useMemo(() => {
        if (!data?.transcription_key) {
            return { type: "pending", message: t("enad_feed_back_state_no_transcription") };
        } else if (data?.transcription_key.split('.')?.slice(-1)?.[0] === 'json') {
            return { type: "ready", message: t("enad_feed_back_state_transcription_ready") };
        } else if (data?.transcription_key.split('.')?.slice(-1)?.[0] === 'downloading') {
            return { type: "in_process", message: t("enad_feed_back_state_in_process_transcription") };
        }
        return { type: "pending", message: '' };
    }, [data?.transcription_key, t]);
    return (
        <div className="flex flex-col md:flex-row justify-center w-full gap-4 p-4 bg-LightBlue-175/20 rounded-4 my-2 text-white">
            <div className='flex flex-1'>
                <div className='flex h-full justify-center items-center w-[32px] mr-4'>
                    <AudioIconSvg />
                </div>
                <div className='flex flex-col flex-1'>
                    <label className='text-4 text-bold'>{name}</label>
                    <label className='text-3'>{date}</label>
                </div>
            </div>
            <div className='flex flex-col flex-1 items-center justify-center'>
                <p className='text-4 text-bold h-fit p-0 m-0'><label className='font-bold mr-1'>{t("state")}:</label>{state?.message}</p>
            </div>
            <div className='flex flex-col flex-1 items-end justify-center'>
                <MenuAudioFileState state={state} />
            </div>
        </div>
    );
}
